import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { lazy, Suspense, useEffect } from "react";
import Home from "@/pages/Home";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Library from "@/pages/Library";
import MediaCenter from "@/pages/MediaCenter";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import { MusicHub, MusicSection, GenrePage } from "@/pages/Catalog";
import TrackPage from "@/pages/TrackPage";
import ArabicRemixes from "@/pages/ArabicRemixes";
import { AlbumDetail, LegacyPackRedirect } from "@/pages/Albums";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/AdminLogin";
import { CheckoutSuccess, CheckoutCancel } from "@/pages/Checkout";
import LegacyRedirect, { LEGACY_PATHS } from "@/pages/LegacyRedirect";
import { Privacy, Terms, Refund } from "@/pages/Legal";
import Cursor from "@/components/Cursor";
import TrackingLoader from "@/components/TrackingLoader";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider } from "@/lib/AuthContext";
import { ConsentProvider } from "@/lib/ConsentContext";
import { CartProvider } from "@/lib/CartContext";
import { CurrencyProvider } from "@/lib/CurrencyContext";
import { LocaleProvider } from "@/lib/LocaleContext";
import { VerificationGateProvider } from "@/lib/VerificationGate";
import { ShoppingSessionProvider } from "@/lib/ShoppingSession";
import { AR_LOCALE_ENABLED } from "@/i18n";
import ConsentBanner from "@/components/ConsentBanner";
import CartDrawer from "@/components/CartDrawer";
import { installGlobalHandlers } from "@/lib/logger";
import { logPageview } from "@/lib/pageviewBeacon";
import { captureFirstTouch } from "@/lib/attribution";
import { useLocation } from "react-router-dom";

// Lazy-loaded admin bundle — keeps homepage JS lean
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const MediaPreview = lazy(() => import("@/pages/MediaPreview"));

function AdminFallback() {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="font-mono text-xs text-white/40 tracking-widest">// LOADING CONTROL PANEL…</div>
    </div>
  );
}

/**
 * Cycle 6 — best-effort pageview beacon.  Listens to react-router route
 * changes and forwards to `logPageview` (which itself is a no-op for
 * paths outside the analytics allowlist).  Rendering nothing keeps the
 * DOM footprint zero.
 */
function PageviewBeacon() {
  const location = useLocation();
  useEffect(() => {
    // Fix 17A — first-touch attribution capture runs on every route
    // change (all landing routes, not just the analytics allowlist) and
    // is consent-independent. It is first-party operational data only.
    captureFirstTouch();
    // Existing consent-gated allowlist pageview beacon — unchanged.
    logPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

/**
 * All customer-facing routes shared by the English root ("/") and Arabic
 * ("/ar") mounts. Route components are unaware of the locale — the URL
 * prefix is transparent because `<LocaleProvider>` flips i18n + `<html>`
 * attributes based on the pathname.
 *
 * The admin routes are intentionally NOT localised — Phase 1 keeps the
 * admin dashboard English-only per approved scope.
 */
function localisedRouteChildren() {
  return [
    <Route key="index" index element={<Home />} />,
    <Route key="login" path="login" element={<Auth mode="login" />} />,
    <Route key="signup" path="signup" element={<Auth mode="signup" />} />,
    <Route key="forgot" path="forgot-password" element={<ForgotPassword />} />,
    <Route key="reset" path="reset-password" element={<ResetPassword />} />,
    <Route key="verify" path="verify-email" element={<VerifyEmail />} />,
    <Route key="library" path="library" element={<Library />} />,
    <Route key="media" path="media" element={<MediaCenter />} />,
    <Route key="blog" path="blog" element={<Blog />} />,
    <Route key="blog-slug" path="blog/:slug" element={<BlogPost />} />,
    <Route key="music" path="music" element={<MusicHub />} />,
    <Route key="music-section" path="music/:section" element={<MusicSection />} />,
    <Route key="genre" path="genre/:slug" element={<GenrePage />} />,
    <Route key="track" path="track/:slug" element={<TrackPage />} />,
    <Route key="arabic-remixes" path="arabic-remixes" element={<ArabicRemixes />} />,
    <Route key="packs" path="packs" element={<LegacyPackRedirect />} />,
    <Route key="pack-slug" path="packs/:slug" element={<LegacyPackRedirect />} />,
    <Route key="music-album" path="music/albums/:slug" element={<AlbumDetail />} />,
    <Route key="cs" path="checkout/success" element={<CheckoutSuccess />} />,
    <Route key="cc" path="checkout/cancel" element={<CheckoutCancel />} />,
    <Route key="privacy" path="privacy" element={<Privacy />} />,
    <Route key="terms" path="terms" element={<Terms />} />,
    <Route key="refund" path="refund" element={<Refund />} />,
  ];
}

function App() {
  useEffect(() => { installGlobalHandlers(); }, []);
  return (
    <div className="App grain">
      <ErrorBoundary>
        <Cursor />
        <ConsentProvider>
        <AuthProvider>
          <CartProvider>
          <CurrencyProvider>
          <ShoppingSessionProvider>
          <VerificationGateProvider>
          <BrowserRouter>
            <LocaleProvider>
              <TrackingLoader />
              <PageviewBeacon />
              <ConsentBanner />
              <CartDrawer />
              <Toaster position="top-center" theme="dark" richColors />
              <Routes>
                <Route path="/">{localisedRouteChildren()}</Route>
                {AR_LOCALE_ENABLED && <Route path="/ar">{localisedRouteChildren()}</Route>}
                <Route path="/admin" element={<AdminLogin />} />
                <Route
                  path="/admin/dashboard"
                  element={
                    <Suspense fallback={<AdminFallback />}>
                      <AdminDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/media-preview"
                  element={
                    <Suspense fallback={<AdminFallback />}>
                      <MediaPreview />
                    </Suspense>
                  }
                />
                {LEGACY_PATHS.map((p) => (<Route key={p} path={p} element={<LegacyRedirect />} />))}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </LocaleProvider>
          </BrowserRouter>
          </VerificationGateProvider>
          </ShoppingSessionProvider>
          </CurrencyProvider>
          </CartProvider>
        </AuthProvider>
        </ConsentProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
